import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { CheckCircle2, RotateCcw, Trash2, Reply } from "lucide-react";

interface Comment {
  id: string;
  authorRole: string;
  fileId?: string;
  trackIndex?: number;
  timestampSec: number;
  text: string;
  status: string;
  category?: string;
  replyTo?: string;
  createdAt: number;
}

interface Props {
  comments: Comment[];
  projectId: string;
  artistId: string;
  isAdmin: boolean;
  fileLabels?: Record<string, string>;
  onSelectComment?: (comment: Comment) => void;
}

const CATEGORIES: Record<string, string> = {
  mix_issue: "Mix Issue",
  vocal_level: "Vocal Level",
  timing: "Timing",
  arrangement: "Arrangement",
  other: "Other",
};

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export default function CommentList({
  comments,
  projectId,
  artistId,
  isAdmin,
  fileLabels = {},
  onSelectComment,
}: Props) {
  const resolveComment = useMutation(api.projectComments.resolveComment);
  const reopenComment = useMutation(api.projectComments.reopenComment);
  const deleteComment = useMutation(api.projectComments.deleteComment);
  const addComment = useMutation(api.projectComments.addProjectComment);

  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");

  // Separate top-level comments and replies
  const topLevel = comments.filter((c) => !c.replyTo);
  const repliesOf = (id: string) => comments.filter((c) => c.replyTo === id);

  async function handleResolve(commentId: string) {
    try {
      await resolveComment({ commentId: commentId as any });
    } catch (err) {
      console.error(err);
    }
  }

  async function handleReopen(commentId: string) {
    try {
      await reopenComment({ commentId: commentId as any });
    } catch (err) {
      console.error(err);
    }
  }

  async function handleDelete(commentId: string) {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await deleteComment({ commentId: commentId as any });
    } catch (err) {
      console.error(err);
    }
  }

  async function handleReply(parentId: string) {
    if (!replyText.trim()) return;
    try {
      await addComment({
        projectId: projectId as any,
        artistId: artistId as any,
        authorRole: isAdmin ? "admin" : "artist",
        timestampSec: 0,
        text: replyText.trim(),
        replyTo: parentId as any,
      });
      setReplyText("");
      setReplyingTo(null);
    } catch (err) {
      console.error(err);
    }
  }

  if (comments.length === 0) {
    return (
      <p className="empty-state">
        No comments yet. Click on the timeline to add a timestamped comment.
      </p>
    );
  }

  return (
    <div className="comment-list">
      {topLevel.map((comment) => {
        const replies = repliesOf(comment.id);
        return (
          <div
            className={`comment-item ${comment.status}`}
            key={comment.id}
          >
            <div className="comment-header">
              <span className="comment-timestamp">
                {comment.timestampSec > 0 && onSelectComment ? (
                  <button
                    aria-label={`Jump to ${formatTime(comment.timestampSec)}`}
                    onClick={() => onSelectComment(comment)}
                    type="button"
                  >
                    {formatTime(comment.timestampSec)}
                  </button>
                ) : comment.timestampSec > 0 ? (
                  formatTime(comment.timestampSec)
                ) : (
                  "General"
                )}
              </span>
              {comment.fileId && fileLabels[comment.fileId] ? (
                <span className="comment-file-label">
                  {fileLabels[comment.fileId]}
                </span>
              ) : null}
              {comment.category ? (
                <span className={`comment-category ${comment.category}`}>
                  {CATEGORIES[comment.category] ?? comment.category}
                </span>
              ) : null}
              <span className={`comment-author ${comment.authorRole}`}>
                {comment.authorRole === "admin" ? "Producer" : "Artist"}
              </span>
              <span className={`comment-status ${comment.status}`}>
                {comment.status}
              </span>
            </div>

            <p className="comment-text">{comment.text}</p>

            <div className="comment-actions">
              {isAdmin && comment.status === "open" ? (
                <button
                  className="comment-action resolve"
                  onClick={() => handleResolve(comment.id)}
                  type="button"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Resolve
                </button>
              ) : null}
              {!isAdmin && comment.status === "resolved" ? (
                <button
                  className="comment-action reopen"
                  onClick={() => handleReopen(comment.id)}
                  type="button"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reopen
                </button>
              ) : null}
              <button
                className="comment-action reply"
                onClick={() =>
                  setReplyingTo(replyingTo === comment.id ? null : comment.id)
                }
                type="button"
              >
                <Reply className="h-3.5 w-3.5" />
                Reply
              </button>
              <button
                className="comment-action delete"
                onClick={() => handleDelete(comment.id)}
                type="button"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Replies */}
            {replies.length > 0 ? (
              <div className="comment-replies">
                {replies.map((reply) => (
                  <div className="comment-reply" key={reply.id}>
                    <span className={`comment-author ${reply.authorRole}`}>
                      {reply.authorRole === "admin" ? "Producer" : "Artist"}
                    </span>
                    <span className="comment-text">{reply.text}</span>
                    <button
                      className="comment-action delete"
                      onClick={() => handleDelete(reply.id)}
                      type="button"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}

            {/* Reply form */}
            {replyingTo === comment.id ? (
              <div className="reply-form">
                <input
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleReply(comment.id);
                  }}
                  placeholder="Write a reply..."
                  value={replyText}
                />
                <button
                  disabled={!replyText.trim()}
                  onClick={() => handleReply(comment.id)}
                  type="button"
                >
                  Send
                </button>
                <button
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyText("");
                  }}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
